import React, {useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import 'core/yupCustomValidations';

import {toast} from 'react-toastify';

import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';
import {ReactComponent as UploadIcon} from 'assets/svg/download.svg';
import {ReactComponent as ScanIcon} from 'assets/svg/scan.svg';
import {FileFormPropsCallback} from 'components/common/XlsxFileForm';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Icon} from 'components/ui-kit/Icon';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {QRScannerModal} from 'components/ui-kit/ScanCode';
import {Select} from 'components/ui-kit/Select';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {PRODUCT_STATUS_COLORS} from 'core/constants/statuses';
import {ProductsPackageType, ProductsStatus} from 'core/dictionaries';
import {
    codesChanged,
    data$,
    dataChanged,
    DocumentsCreateGate,
    file$,
    fileChanged,
    sendDropOutFx,
    sendUtilisationFx,
    stepChanged,
} from 'models/documents/create';
import {GridFieldConfig} from 'types';
import {CodeResponseType, ValidatedProductsGridAccessors} from 'types/validation';
import {handleNil} from 'utils/handleNil';

import {PackageTypesOptions} from 'utils/packageTypes-mappers';
import {mapArrayToI18nOptions} from 'utils/select';

import {hasOnlyLatinLetters} from 'utils/string';

import {
    ActionsContainer,
    ControlContainer,
    CountContainer,
    HeadContainer,
    NoCodesContent,
    StepNavigationContainer,
    Wrapper,
} from './styled';
import {XlsxFileModal} from './UploadModal';

const getConfig = (t: TFunction): GridFieldConfig[] => [
    {
        id: ValidatedProductsGridAccessors.name,
        header: t('pages.products.grid.productName'),
        maxWidth: 220,
    },
    {
        id: ValidatedProductsGridAccessors.code,
        header: t('pages.products.grid.code'),
    },
    {
        id: ValidatedProductsGridAccessors.packageType,
        header: t('pages.products.grid.package'),
        filter: (
            <Select
                isClearable
                options={mapArrayToI18nOptions(
                    PackageTypesOptions,
                    'dictionaries.products.packageType',
                )}
            />
        ),
        width: 170,
    },
    {
        id: ValidatedProductsGridAccessors.status,
        header: t('pages.products.grid.status'),
        width: 200,
    },
    {
        id: 'action',
        header: t('pages.products.grid.action'),
        width: 80,
    },
];

const convertData = (
    data: CodeResponseType['info'][] | undefined,
    t: TFunction,
): Partial<CodeResponseType['info'] & {action: React.ReactNode}>[] => {
    if (!data) return [];

    return data.map((elem) => ({
        [ValidatedProductsGridAccessors.name]: handleNil(elem.productName),
        [ValidatedProductsGridAccessors.code]: handleNil(elem.identityCode),
        [ValidatedProductsGridAccessors.status]: handleNil(elem.status, (value: ProductsStatus) => (
            <StatusWrapper color={PRODUCT_STATUS_COLORS[value ?? 'DEFAULT']}>
                {t(`dictionaries.products.status.${value}`)}
            </StatusWrapper>
        )),
        [ValidatedProductsGridAccessors.packageType]: handleNil(
            elem.packagingType,
            (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
        ),
        action: (
            <ActionsContainer>
                <Icon
                    source={CrossIcon}
                    width={16}
                    height={16}
                    fillColor="#FF4B55"
                    onClick={() => dataChanged(elem)}
                />
            </ActionsContainer>
        ),
    }));
};

type Props = {
    createDocument: Function;
    prevStep?: number;
    validation: (
        markingCode: string,
        data: CodeResponseType['info'][],
    ) => Promise<{errors: string[]; codeData: CodeResponseType['info'] | null}>;
    submitLabel?: string;
};
export const ProductCodes: React.FC<Props> = ({
    createDocument,
    validation,
    submitLabel,
    prevStep,
}) => {
    const {t} = useTranslation();
    const sendDropOutPending = useStore(sendDropOutFx.pending);
    const sendUtilisationPending = useStore(sendUtilisationFx.pending);

    const data = useStore(data$);

    const columns = useMemo(() => mapTableColumns(getConfig(t)), [t]);
    const mappedData = React.useMemo(() => convertData(data, t), [data, t]);

    const [scanShow, setScanShow] = React.useState(false);
    const [uploadShow, setUploadShow] = React.useState(false);
    const [pending, setPending] = React.useState(false);

    const onEnterCode = async (code: string) => {
        try {
            setPending(true);
            if (!hasOnlyLatinLetters(code)) {
                toast.warning(t('components.scan.layoutWarn'));
                return;
            }

            const {errors, codeData} = await validation(code, data);
            if (errors.length) {
                toast.error(
                    <div>
                        <strong>Errors</strong>
                        <ul style={{margin: '6px 0'}}>
                            {errors.map((e) => (
                                <li key={e}>{e}</li>
                            ))}
                        </ul>
                    </div>,
                );
                return;
            }
            if (codeData) dataChanged(codeData);
        } catch (e) {
            console.error(e);
            toast.error(e.message ?? 'Invalid code');
        } finally {
            setPending(false);
        }
    };

    const file = useStore(file$);
    const handleFileChanged = ({data, file: newFile, isValid}: FileFormPropsCallback) => {
        codesChanged(isValid ? data.map(({code}) => code) : []);
        fileChanged(newFile);
    };
    const resetUpload = () => {
        setUploadShow(false);
        codesChanged([]);
        fileChanged(null);
    };

    const EmptyMessage = () => (
        <NoCodesContent>
            <h3>{t('components.table.Scan.title')}</h3>
            <span>{t('components.table.Scan.description')}</span>
        </NoCodesContent>
    );

    return (
        <>
            <Wrapper>
                <HeadContainer>
                    <ControlContainer>
                        <Button onClick={() => setScanShow(true)}>
                            {t('components.scan.button')}
                            <Icon source={ScanIcon} width={20} height={20} />
                        </Button>
                        <Button variant={ButtonVariant.Outline} onClick={() => setUploadShow(true)}>
                            {t('components.upload.button')}
                            <Icon source={UploadIcon} width={16} height={16} />
                        </Button>
                    </ControlContainer>
                    <StepNavigationContainer>
                        <Button
                            style={{width: 'auto'}}
                            variant={ButtonVariant.Secondary}
                            onClick={() => stepChanged(prevStep ?? 0)}
                        >
                            {t('components.form.prevStep')}
                        </Button>
                        <Button
                            style={{width: 'auto'}}
                            variant={ButtonVariant.Normal}
                            onClick={() => createDocument()}
                            disabled={!data.length || sendDropOutPending || sendUtilisationPending}
                        >
                            {submitLabel ?? t('pages.documents.form.submit')}
                            {!!data.length && <CountContainer>{data.length}</CountContainer>}
                        </Button>
                    </StepNavigationContainer>
                </HeadContainer>
                <Table
                    emptyStateComponent={EmptyMessage()}
                    columns={columns}
                    data={mappedData}
                    hideSetting
                />
            </Wrapper>
            <QRScannerModal
                open={scanShow}
                pending={pending}
                onClose={() => setScanShow(false)}
                onScan={onEnterCode}
                onManual={onEnterCode}
            />
            <XlsxFileModal
                open={uploadShow}
                onClose={resetUpload}
                onFileChange={handleFileChanged}
                submit={(valid) => (
                    <Button onClick={() => createDocument()} disabled={!valid}>
                        {submitLabel ?? t('pages.documents.form.submit')}
                    </Button>
                )}
                initialFile={file}
            />
        </>
    );
};
